// English (en) — source catalog for the intake widget.
//
// This is a PURE EXTRACTION of the copy that previously lived inline in the
// widget stage components. Values here must stay byte-identical to the rendered
// English output so `en` clinics (incl. the demo clinic used by
// `npm run proof:critical`) are unchanged.
//
// Keys are grouped by surface. Rich display titles that mix emphasis (<em>) or a
// line break are decomposed into plain-string segments (line1 / pre / em / post)
// so `t(key)` always returns a plain string and the markup stays in the
// component. Interpolation tokens use `{name}` syntax (see `t(key, params)`).

export const en = {
  // ── Shared ────────────────────────────────────────────────────────────────
  'common.continue': 'Continue',
  'common.theClinic': 'the clinic',

  // ── Launcher pill + modal shell (EmbedShell) ────────────────────────────────
  'launcher.label': 'Get my selfie skin report',
  'launcher.ariaOpen': 'Open skin report intake — get my selfie skin report',
  'shell.dialogAria': 'Skin report intake',
  'shell.closeAria': 'Close intake',

  // ── Aperture / config states (Widget) ───────────────────────────────────────
  'aperture.label': 'Preparing your report',
  'aperture.sub': 'The instrument is warming up',
  'config.error': 'Unable to load clinic information. Please refresh the page.',

  // ── aria-live step announcements (Widget) ───────────────────────────────────
  'aria.toProfile': 'Moving to step 2 of 6: Your profile',
  'aria.toPhoto': 'Moving to step 3 of 6: Your photo',
  'aria.toPreparing': 'Moving to step 4 of 6: Preparing your report',
  'aria.toContact': 'Moving to step 5 of 6: Where to send your report',
  'aria.toSubmitting': 'Step 6 of 6: Sending your request',
  'aria.submitted': 'Your request has been submitted. Your report will arrive by email.',
  'aria.backToConsent': 'Returning to step 1 of 6: Consent screen',
  'aria.backToDetails': 'Returning to step 2 of 6: Your details',
  'aria.backToPhoto': 'Returning to step 3 of 6: Your photo',
  'aria.backToContact': 'Returning to step 5 of 6: Your contact details',
  'aria.closeSubmitted': 'Your report request has been submitted. Your report will arrive by email.',

  // ── Stage 01 — Consent ──────────────────────────────────────────────────────
  'consent.constellation.armed': 'Instrument armed',
  'consent.constellation.begin': 'Your profile begins here',
  'consent.title.line1': 'Your personal',
  'consent.title.line2Pre': 'skin ',
  'consent.title.line2Em': 'report',
  'consent.sub':
    'Answer a few quick questions and add an optional selfie. We\u2019ll prepare a personalized skin report and send it to your inbox within minutes.',
  'consent.formAria': 'Consent to personalized skin report',
  'consent.privacy':
    'Your photo and answers are used only to prepare your report and respond to your request. Never sold.',
  'consent.agree':
    'I agree to the collection and use of my photo and answers for this personalized skin report. ',
  'consent.privacyLink': 'Privacy policy \u2197',
  'consent.desc': 'You must agree before continuing.',
  'consent.cta': 'START MY REPORT',
  'consent.ctaHint': 'Please check the consent box above to enable this button.',
  'consent.legal':
    'By continuing, you confirm you are 18 or older. This report offers cosmetic guidance and does not replace professional medical advice.',

  // ── Stage 02 — Profile + Q1–Q4 ──────────────────────────────────────────────
  'profile.backToConsentAria': 'Go back to consent',
  'profile.prevQuestionAria': 'Go to previous question',
  'profile.title.pre': 'About ',
  'profile.title.em': 'your',
  'profile.title.post': ' skin',
  'profile.sub': 'A few details help personalize your report.',
  'profile.ageLabel': 'Age range',
  'profile.required': '*',
  'profile.optional': 'optional',
  'profile.ageAria': 'Age range',
  'profile.skinTypeLabel': 'Skin type',
  'profile.skinTypeAria': 'Skin type',
  'profile.concernsLabel': 'Skin concerns',
  'profile.concernsHelp': 'Select all that apply',
  'profile.concernsAria': 'Skin concerns',
  'profile.ageRanges': ['18\u201324', '25\u201334', '35\u201344', '45\u201354', '55+'],
  'profile.skinTypes': ['Dry', 'Oily', 'Combination', 'Normal', 'Sensitive'],
  'profile.skinConcerns': [
    'Breakouts', 'Dryness', 'Uneven tone', 'Fine lines',
    'Dark spots / pigmentation', 'Sensitivity', 'Redness', 'Dullness', 'Other',
  ],

  'q.step.profile': 'Step 2 of 6 — Your profile',
  'q.step.q1': 'Question 1 of 4',
  'q.step.q2': 'Question 2 of 4',
  'q.step.q3': 'Question 3 of 4',
  'q.step.q4': 'Question 4 of 4',

  'q1.prompt': 'What would you most like to improve about your skin?',
  'q2.prompt': 'What result would you most like to see?',
  'q3.prompt': 'Have you had any aesthetic treatments before?',
  'q4.prompt': 'What investment range feels comfortable?',
  'q4.helper': 'This helps us suggest realistic options.',

  'q1.options': [
    'Fine lines or wrinkles',
    'Dark spots or uneven tone',
    'Dull or tired-looking skin',
    'Breakouts or uneven texture',
    'Preventing early signs of aging',
  ],
  'q2.options': [
    'Smoother skin',
    'Brighter, more glowing skin',
    'A more rested, refreshed look',
    'Fuller lips or better facial balance',
    'Healthier-looking skin',
  ],
  'q3.options': ['No, not yet', 'Once or twice', 'Occasionally', 'Regularly'],
  'q4.options': ['Under $300', '$300\u2013$1,000', '$1,000+'],

  // ── Stage 03 — Photo ────────────────────────────────────────────────────────
  'photo.backAria': 'Go back to details',
  'photo.step': 'Step 3 of 6 — Your Photo',
  'photo.title.usePhoto': 'Use this photo?',
  'photo.title.reading': 'Reading your photo',
  'photo.title.default': 'A clear look at your skin',
  'photo.sub.camera': 'Make sure your face is clear and evenly lit.',
  'photo.sub.upload': 'Holding still — mapping facial landmarks.',
  'photo.sub.showCamera':
    'Good lighting helps us read fine detail. Your photo is used only to prepare your report.',
  'photo.sub.default':
    'Choose a recent, well-lit photo of your face. It\'s used once, only for your report.',
  'photo.status.waiting': 'Waiting for camera access\u2026',
  'photo.status.ready': 'Camera ready',
  'photo.status.denied': 'Camera access denied — please upload a photo instead',
  'photo.status.none': 'No camera found — please upload a photo instead',
  'photo.cue.align': 'Align your face within the guide',
  'photo.cue.allow': 'Allow camera access to begin',
  'photo.upload.aria': 'Choose a photo from your device',
  'photo.upload.tap': 'Tap to choose a photo',
  'photo.upload.formats': 'JPG or PNG, up to 10 MB',
  'photo.error.format': 'Please choose a JPG or PNG image.',
  'photo.error.size': 'Image size must be under 10 MB.',
  'photo.previewAlt': 'Your selfie preview',
  'photo.captureAria': 'Take photo',
  'photo.retake': 'Retake photo',
  'photo.chooseDifferent': 'Choose a different photo',
  'photo.seg.aria': 'Photo input method',
  'photo.seg.camera': 'Camera',
  'photo.seg.upload': 'Upload',
  'photo.skip': 'Continue without a selfie →',
  'photo.skipAria': 'Continue without a selfie',

  // ── Stage 04 — Scan preview (preparation framing 4a) ────────────────────────
  'scan.step': 'Preparing your report',
  'scan.p0.titlePre': 'Reading your ',
  'scan.p0.titleEm': 'photo',
  'scan.p0.narration': 'Preparing your image on this device.',
  'scan.p1.titlePre': 'Preparing your ',
  'scan.p1.titleEm': 'skin report',
  'scan.p1.narration': 'Mapping facial landmarks to personalize your report.',
  'scan.p2.titlePre': 'Almost ',
  'scan.p2.titleEm': 'ready',
  'scan.p2.narration': 'One last step to send your report.',
  'scan.layers': ['Tone', 'Texture', 'Detail'],
  'scan.foot': 'Your report is prepared privately and sent after the next step.',
  'scan.live': 'Preparing your report on your device. One last step to send your report.',

  // ── Stage 05 — Contact ──────────────────────────────────────────────────────
  'contact.backAria': 'Go back to your photo',
  'contact.step': 'Step 5 of 6 — Where to send it',
  'contact.title.line1': 'Where should we',
  'contact.title.line2Pre': 'send your ',
  'contact.title.line2Em': 'report?',
  'contact.sub': 'Your report is prepared and sent privately.',
  'contact.formAria': 'Your contact details',
  'contact.firstName': 'First name',
  'contact.email': 'Email',
  'contact.phone': 'Phone',
  'contact.firstNamePlaceholder': 'Your first name',
  'contact.emailPlaceholder': 'your@example.com',
  'contact.phonePlaceholder': 'Enter your number',
  'contact.emailHelp': 'Your report arrives here.',
  'contact.error.firstName': 'First name is required',
  'contact.error.email': 'Check the email address — your report can\'t reach you without it.',
  'contact.error.phone': 'Please enter a valid phone number',
  'contact.countryCodeAria': 'Country code',
  'contact.cta': 'REQUEST MY REPORT',
  'contact.legal':
    'By submitting, you agree that {brand} may contact you by email or text about your report and appointment options. Msg/data rates may apply. Reply STOP to opt out.',

  // ── Stage 06 — Submitting ───────────────────────────────────────────────────
  'submit.step': 'Step 6 of 6 — Sending your request',
  'submit.title.pre': 'Sending your request ',
  'submit.title.em': 'securely',
  'submit.narration': 'Sending your request securely.',
  'submit.foot': 'Prepared privately and sent to your inbox.',
  'submit.error.4xx': 'Something went wrong. Please check your details and try again.',
  'submit.error.review': 'Review your details',
  'submit.error.network': 'We\'re having trouble connecting. Your information is safe.',
  'submit.error.tryAgain': 'Try again',
  'submit.error.cap': 'You\'ve reached today\'s demo limit. Please try again tomorrow.',
  'submit.live': 'Sending your request securely. Prepared privately and sent to your inbox.',

  // ── Stage 07 — Success ──────────────────────────────────────────────────────
  'success.live':
    'Your report request has been submitted successfully. Your personalized skin report will be sent to your email address shortly.',
  'success.title.line1': 'Your report is',
  'success.title.em': 'on its way',
  'success.sub.pre': 'We\u2019ve sent a confirmation to ',
  'success.sub.post': '. Your personalized skin report will follow within minutes.',
  'success.talk': 'Prefer to talk it through?',
  'success.book': 'Speak to our team →',
  'success.bookAria': 'Book a consultation with {brand} (opens in new tab)',
  'success.help': 'Anything unclear? ',
  'success.contactLink': 'Contact us',
  'success.emailAria': 'Email {brand}',
  'success.barStatus': 'Report sent',
};
